import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { DASHBOARD_PATH } from 'config';
import { freshReturnPath, isSafeReturnPath, peekReturnPathEntry, takeReturnPath, type ReturnPathEntry } from 'shared/navigation/returnPath';
// TODO: restaurar cuando se reactive flujo pre-auth
// import { submitPendingShipment } from 'utils/shipment-submission';

// ==============================|| GUEST GUARD ||============================== //

// hooks/useAuth is still a legacy .js module with no types of its own; this is the slice of
// it this guard consumes, declared at the seam instead of spreading `any` through the file —
// same call as AuthGuard.tsx's AuthGuardAuth.
interface GuestGuardAuth {
  isLoggedIn: boolean;
}

interface GuestGuardProps {
  children: ReactNode;
}

export default function GuestGuard({ children }: GuestGuardProps) {
  const { isLoggedIn } = useAuth() as unknown as GuestGuardAuth;
  const navigate = useNavigate();
  const location = useLocation();
  // What was remembered while this guard stood over the auth screens with nobody signed in. Another tab
  // can take the stored entry before this one reads it: the tab that asked for the email link stays
  // open under its own GuestGuard and redirects the moment the session exists, which can be before the
  // tab the link opened gets its own. Holding the entry from the start leaves that tab a page to go to.
  const seenSignedOut = useRef<ReturnPathEntry | null>(null);

  useEffect(() => {
    const handleLoginRedirect = async () => {
      if (isLoggedIn) {
        // TODO: lógica pendingShipment desactivada temporalmente.
        // Restaurar cuando /nuevo-envio vuelva a ser ruta pública (flujo pre-auth):
        // el usuario arma el envío sin sesión, al submitir se guarda en localStorage,
        // y acá post-login se auto-envía o redirige de vuelta a /nuevo-envio.
        //
        // const pendingData = localStorage.getItem('pendingShipment');
        // if (pendingData) {
        //   if (!user?.pilotEnabled) {
        //     localStorage.removeItem('pendingShipment');
        //     navigate(DASHBOARD_PATH, { replace: true });
        //     return;
        //   }
        //   try {
        //     await submitPendingShipment();
        //     localStorage.setItem('shipmentCreated', 'true');
        //     navigate('/shipments', { replace: true });
        //   } catch (error) {
        //     console.error('Auto-submit failed', error);
        //     navigate('/nuevo-envio', { replace: true });
        //   }
        // } else {
        //   const from = location.state?.from?.pathname || DASHBOARD_PATH;
        //   navigate(from, { replace: true });
        // }

        // NEX-1428: AuthGuard.tsx stores the WHOLE Location as `state.from` (pathname + search +
        // hash) before bouncing a logged-out visitor to /login. Reading only `.pathname` here
        // dropped the query string on the way back — a logged-out client clicking an emailed
        // link such as /shipments/123?pin=locked or /payment/success?shipmentId=456 landed on the
        // right PAGE post-login, but silently lost the param driving that page's actual behavior.
        //
        // NEX-1585: that state is gone when the sign-in left the tab or reloaded the page — the email
        // link opens a new tab, a mobile social login reloads — so AuthGuard also remembers the page in
        // storage (shared/navigation/returnPath). In order: the router state (fresher), the entry this
        // guard held while nobody was signed in (unless it expired meanwhile), the stored entry. The
        // stored one is always taken, so a stale one cannot surface on a later login, and only a
        // same-origin path is followed, whichever of the three it comes from.
        const stored = takeReturnPath();
        const held = freshReturnPath(seenSignedOut.current);
        seenSignedOut.current = null;
        const from = location.state?.from;
        const fromPath = from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : null;
        navigate([fromPath, held, stored].find(isSafeReturnPath) ?? DASHBOARD_PATH, { replace: true });
      } else {
        // Nobody is signed in: keep what is remembered, a newer entry replacing an older one. Nothing is
        // taken here; the sign-in does that.
        seenSignedOut.current = peekReturnPathEntry() ?? seenSignedOut.current;
      }
    };

    handleLoginRedirect();
  }, [isLoggedIn, navigate, location]);

  return children;
}
